import { defineStore } from "pinia"
import axios from "axios"
import type { Freq, ConditionBlock, ConfigGraphCard } from "@types"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"
import type { Services } from "@services"
import { computed, ref, inject, watch, nextTick } from "vue"
import { useRoute, useRouter } from "vue-router"
import { SOURCES_TYPES } from "@core/constants"
import { useEventBus, watchDebounced, useStorage } from "@vueuse/core"
import {
    handleBuildingCondition,
    handleSourceCondition,
    handleUserCondition,
} from "@modules/data/helpers"
import { ServiceError } from "@core/exceptions"
import moment from "moment"
import { useFetchWrapper } from "@core/composables"
import {
    evaluateVisibleConditionsBlocks,
    VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
    VISIBLE_CONDITIONS_COMBINE_GRAPH_DEFAULT,
} from "@modules/data/utils/evaluateVisibleConditionsBlocks"
import { clampGraphCardFreqsToServed } from "@modules/data/utils/servedGraphFreqs"
import { urlPorteLesParametresDeGraphe } from "@modules/data/utils/parametresUrlGraphe"
import { laPageCompteurV2EstServie } from "@modules/data/utils/pageCompteurV2"
import { useInterrupteursStore } from "@core/stores/interrupteurs.store"

/** Préfixe des identifiants des graphiques réglage chaufferie (ConfigGraph + réponse DAPI). */
export const GRAPH_REGLAGE_CHAUFFERIE_PREFIX = "reglage_chaufferie_"

/** Liste des identifiants des graphiques réglage chaufferie (ordre d'affichage). */
export const GRAPH_REGLAGE_CHAUFFERIE = [
    "reglage_chaufferie_temperature_reduit_nuit",
    "reglage_chaufferie_balancing",
    "reglage_chaufferie_profil_daily",
    "reglage_chaufferie_profil_weekly_hour",
    "reglage_chaufferie_profil_weekly_day",
    "reglage_chaufferie_curve_heat",
] as const

type RequestQueue = {
    type: "building" | "source"
    graph_id: string
    plotly_graph_id: string
    freq: Freq
    date_from: Date
    date_to: Date
    abordController: AbortController
    callback: (data: any) => void
}

type GraphSlot = Pick<RequestQueue, "type" | "graph_id" | "plotly_graph_id">

function sameGraphSlot(a: GraphSlot, b: GraphSlot): boolean {
    return (
        a.type === b.type &&
        a.graph_id === b.graph_id &&
        a.plotly_graph_id === b.plotly_graph_id
    )
}

export const useGraphStore = defineStore("graph", () => {
    const buildingStore = useBuildingStore()
    const sourceStore = useSourceStore()
    const router = useRouter()
    const route = useRoute()
    const bus = useEventBus<string>("news")
    const busNotification = useEventBus<{
        severity: "warn" | "error" | "success" | "info"
        title: string
        message: string
        life?: number
    }>("notification")
    const storageBuilding = useStorage<
        { graphs: string[]; _id_building: string }[]
    >("graph-building", [])
    const storageSource = useStorage<
        { graphs: string[]; _id_source: string }[]
    >("graph-source", [])

    /** Config graphiques plate (id → ConfigGraphCard), remplie par setGraphList. */
    const graphConfig = ref<Record<string, ConfigGraphCard>>({})
    /** Config par catégorie (GRAPH_BUILDING, GRAPH_PDL, …), remplie par setGraphList. */
    const graphConfigByCategory = ref<
        Record<string, Record<string, ConfigGraphCard>>
    >({
        GRAPH_BUILDING: {},
        GRAPH_PDL: {},
        GRAPH_PCE: {},
        GRAPH_REGAZ: {},
        GRAPH_ELECSAVOIE: {},
        GRAPH_ELECSTRASBOURG: {},
        GRAPH_IOT: {},
        GRAPH_RCU: {},
        GRAPH_WEATHER_STATION: {},
        GRAPH_CONSO_CHAUFFAGE: {},
        GRAPH_REGLAGE_CHAUFFERIE: {},
        GRAPH_EAU: {},
    })

    /**
     * Récupère la liste des graphiques depuis l'API et l'enregistre dans le store.
     */
    const {
        fetch: fetchGraphList,
        isFetching: isFetchingGraphList,
        hasError: hasErrorGraphList,
        error: errorGraphList,
    } = useFetchWrapper(async () => {
        const graphList = await $getGraphList()
        setGraphList(graphList)
    })

    /**
     * Enregistre la liste des graphiques (appelé après fetchGraphList ou pour tests).
     *
     * Les fréquences de chaque graphique sont bornées aux grains réellement
     * servis par dapi (DEV-1481 : la config `ConfigGraph` promettait raw/hourly
     * pour la durée de dépassement, servie en daily seul → cadres vides).
     */
    function setGraphList(graphList: ConfigGraphCard[]) {
        const flat: Record<string, ConfigGraphCard> = {}
        const byCategory = { ...graphConfigByCategory.value }
        graphList.forEach((graphRaw) => {
            const graph = clampGraphCardFreqsToServed(graphRaw)
            flat[graph.id] = graph
            if (graph.id.startsWith(SOURCES_TYPES.PDL)) {
                byCategory.GRAPH_PDL[graph.id] = graph
            } else if (graph.id.startsWith(SOURCES_TYPES.PCE)) {
                byCategory.GRAPH_PCE[graph.id] = graph
            } else if (graph.id.startsWith(SOURCES_TYPES.REGAZ)) {
                byCategory.GRAPH_REGAZ[graph.id] = graph
            } else if (graph.id.startsWith(SOURCES_TYPES.ELECSAVOIE)) {
                byCategory.GRAPH_ELECSAVOIE[graph.id] = graph
            } else if (graph.id.startsWith(SOURCES_TYPES.ELECSTRASBOURG)) {
                byCategory.GRAPH_ELECSTRASBOURG[graph.id] = graph
            } else if (graph.id.startsWith("building")) {
                byCategory.GRAPH_BUILDING[graph.id] = graph
            } else if (graph.id.startsWith("sensor")) {
                byCategory.GRAPH_IOT[graph.id] = graph
            } else if (graph.id.startsWith("weather_station")) {
                byCategory.GRAPH_WEATHER_STATION[graph.id] = graph
            } else if (graph.id.startsWith("rcu")) {
                byCategory.GRAPH_RCU[graph.id] = graph
            } else if (graph.id.startsWith("chauffage_conso")) {
                byCategory.GRAPH_CONSO_CHAUFFAGE[graph.id] = graph
            } else if (graph.id.startsWith(GRAPH_REGLAGE_CHAUFFERIE_PREFIX)) {
                byCategory.GRAPH_REGLAGE_CHAUFFERIE[graph.id] = graph
            } else if (graph.id.startsWith("eau")) {
                byCategory.GRAPH_EAU[graph.id] = graph
            }
        })
        graphConfig.value = flat
        graphConfigByCategory.value = byCategory
    }

    const isGraphListLoaded = computed(
        () => Object.keys(graphConfig.value).length > 0
    )

    function getGraph(id: string): ConfigGraphCard | undefined {
        return graphConfig.value[id]
    }

    function getGraphBuildingConfig(): Record<string, ConfigGraphCard> {
        return graphConfigByCategory.value.GRAPH_BUILDING ?? {}
    }

    function getGraphConfigByCategory(
        category: string
    ): Record<string, ConfigGraphCard> {
        return graphConfigByCategory.value[category] ?? {}
    }

    /** Configuration spécifique pour certaines pages (ex. conso chauffage, réglage chaufferie). */
    const CONFIG_GRAPH_PAGE = computed(() => [
        {
            pathInclude: "/energy-manager/chauffage/consommation",
            graphList: Object.values(
                graphConfigByCategory.value.GRAPH_CONSO_CHAUFFAGE ?? {}
            ).map((graph) => graph.id),
        },
        {
            pathInclude: "chauffage/reglage-chaufferie",
            graphList: Object.values(
                graphConfigByCategory.value.GRAPH_REGLAGE_CHAUFFERIE ?? {}
            ).map((graph) => graph.id),
        },
    ])

    const { $createGraphByBuildingId, $createGraphBySourceId, $getGraphList } =
        inject<Services>("services")!

    // =============
    // States
    // =============
    /** Contexte source pour la page source (storage et fetchSourceChart). Mis à jour par PageSourceData au mount/unmount. */
    const currentSourceId = ref<string | null>(null)

    /**
     * Vrai quand la route courante rend la page « Données du compteur » v2
     * (DEV-1508 lot 3c). Ce store est PARTAGÉ, et son surveillant de période
     * écrit `freq`, `date_from` et `date_to` dans l'URL dès sa création : sans
     * cette question, la route standard ARMÉE héritait d'une période de trois
     * mois qu'elle n'affiche plus et que personne n'avait choisie. Désarmé,
     * l'interrupteur rend `false` et rien ne change (E11).
     */
    const pageCompteurV2Servie = computed(() =>
        laPageCompteurV2EstServie({
            arme: useInterrupteursStore().enedisPageDonneesV2Arme,
            source: currentSourceId.value
                ? (sourceStore.getSourceById(currentSourceId.value) ?? null)
                : null,
        })
    )
    // Liste des identifiants des cartes de graphiques sélectionnées
    const graphs = ref<string[]>([])
    // La période sélectionnée
    const date_from = route.query.date_from
        ? new Date(route.query.date_from as string)
        : new Date(new Date().setMonth(new Date().getMonth() - 3))
    const date_to = route.query.date_to
        ? new Date(route.query.date_to as string)
        : new Date()
    const period = ref<[Date, Date]>([date_from, date_to])
    // La fréquence de pas de temps des données sélectionnées
    const freq = ref<Freq>(
        route.query.freq
            ? (route.query.freq as Freq)
            : route.path.includes("building")
              ? "daily"
              : "raw"
    )
    // Liste des graphiques à afficher via l'URL
    const getGraphQuery = computed(() => {
        if (!route.query.graph) return []

        // Si le paramètre est une string, on le split en tableau
        if (typeof route.query.graph === "string") {
            return (route.query.graph as string).split(",")
        }

        // Si c'est un tableau on le retourne tel quel
        if (Array.isArray(route.query.graph)) {
            return route.query.graph
        }
        // Si c'est autre chose on retourne un tableau vide
        return []
    })
    // permet de ne pas autoriser certaines actions (export, redirection) (pour des apps externes ex:VCS)
    const isActionAuthorised = computed(() => {
        return route.query.action !== "false"
    })

    // Queue permettant de gérer les appels API
    const queue = ref<RequestQueue[]>([])
    const isLoading = ref<boolean>(false)
    /**
     * Dernière émission `graph:updated` pour la même période+fréquence (fenêtre courte uniquement,
     * pour éviter un double emit dans le même tick / batch watchDebounced + maxWait sans bloquer un retour utilisateur plus tard).
     */
    const lastGraphContextEmit = ref<{ key: string; at: number } | null>(null)

    // =============
    // Getter & helpers
    // =============
    function setCurrentSourceId(id: string | null) {
        currentSourceId.value = id
    }

    /**
     * Identifiant bâtiment pour la persistance des graphiques (localStorage).
     * Utilise la route tant que le store bâtiment n’est pas encore hydraté (reload),
     * pour ne pas écraser les graphiques sauvegardés avec une liste vide.
     */
    const buildingIdForGraphStorage = computed(() => {
        const p = route.params._id_building
        const fromRoute = Array.isArray(p) ? p[0] : (p as string | undefined)
        const fromStore = buildingStore.building?._id
        if (fromStore && fromRoute && fromStore !== fromRoute) {
            return fromRoute
        }
        return fromStore ?? fromRoute
    })

    /**
     * Récupère les données de stockage du bâtiment sélectionné
     * Retourne null si on est sur la page de consommation de chauffage (pas de stockage nécessaire)
     */
    const getBuildingStorageById = computed(() => {
        // Pour les pages spécifiques, on ne retourne pas de stockage
        const config = CONFIG_GRAPH_PAGE.value.find((config) =>
            route.path.includes(config.pathInclude)
        )
        if (config) {
            return undefined
        }

        const id = buildingIdForGraphStorage.value
        if (!id) return undefined

        return storageBuilding.value.find((s) => s._id_building === id)
    })

    /**
     * Cartes graphiques « bâtiment » visibles pour le bâtiment courant (conditions + données).
     * Même logique qu’historiquement pour la fin de {@link getOptimalBuildingGraph} sans query ni localStorage.
     */
    function getBuildingGraphCardIdsVisibleForBuilding(): string[] {
        const building = buildingStore.building
        const buildingSources = buildingStore.getSources
        if (!building?.sources) return []

        return Object.values(getGraphBuildingConfig())
            .filter((cardGraph) => {
                function isCardVisible(
                    cardGraphInner: ConfigGraphCard
                ): boolean {
                    return evaluateVisibleConditionsBlocks(
                        cardGraphInner.visible_conditions,
                        cardGraphInner.visible_conditions_combine,
                        VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
                        (condition: ConditionBlock) => {
                            if (condition.origin === "user") {
                                return handleUserCondition(condition)
                            }
                            if (!building) return false
                            if (condition.origin === "building") {
                                return handleBuildingCondition(
                                    building,
                                    condition
                                )
                            }
                            return handleSourceCondition(
                                buildingSources,
                                condition
                            )
                        }
                    )
                }

                function isGraphVisible(graph: any): boolean {
                    return evaluateVisibleConditionsBlocks(
                        graph.visible_conditions,
                        graph.visible_conditions_combine,
                        VISIBLE_CONDITIONS_COMBINE_GRAPH_DEFAULT,
                        (condition: ConditionBlock) => {
                            if (condition.origin === "user") {
                                return handleUserCondition(condition)
                            }
                            if (!building) return false
                            if (condition.origin === "building") {
                                return handleBuildingCondition(
                                    building,
                                    condition
                                )
                            }
                            return handleSourceCondition(
                                buildingSources,
                                condition
                            )
                        }
                    )
                }

                if (!isCardVisible(cardGraph)) {
                    return false
                }

                return cardGraph.graph_list.some(isGraphVisible)
            })
            .map((cardGraph) => cardGraph.id)
    }

    /**
     * Récupère la liste des graphiques avec des données disponibles pour le bâtiment sélectionné.
     * Si aucun graphique se trouve dans le localStorage, on retourne la liste des graphiques par défaut
     * qui sont visibles pour le bâtiment sélectionné.
     */
    const getOptimalBuildingGraph = computed<string[]>(() => {
        // Pour les pages spécifiques, on retourne la configuration définie
        const config = CONFIG_GRAPH_PAGE.value.find((config) =>
            route.path.includes(config.pathInclude)
        )
        if (config && config.graphList?.length) {
            return config.graphList
        }

        // Avant toute garde sur building.sources : au reload le bâtiment peut
        // ne pas être encore chargé alors que le localStorage est déjà lisible (route + clé).
        if (
            getBuildingStorageById.value?.graphs?.length &&
            !getGraphQuery.value?.length
        ) {
            return getBuildingStorageById.value.graphs ?? []
        }

        const optimalVisible = getBuildingGraphCardIdsVisibleForBuilding()

        if (getGraphQuery.value?.length > 0) {
            const fromQuery = getGraphQuery.value.filter(
                (g): g is string => g !== null
            )
            return fromQuery.filter((id) => optimalVisible.includes(id))
        }

        return optimalVisible
    })

    /**
     * Récupère la liste des graphiques avec des données disponibles pour la source sélectionnée depuis le localStorage
     */
    const getOptimalSourceGraph = computed(() => {
        if (!currentSourceId.value) return []
        return (
            storageSource.value.find(
                (s) => s._id_source === currentSourceId.value
            )?.graphs ?? []
        )
    })

    // =============
    // Actions
    // =============

    /**
     * Annule toutes les requêtes graphiques en file (y compris celle en cours d’exécution en tête de file)
     * via AbortSignal, puis vide la file. Les handlers `finally` de `processQueue` retirent encore
     * l’entrée en cours si besoin (`indexOf`).
     */
    function abortAllQueuedGraphRequests() {
        const pending = [...queue.value]
        queue.value = []
        for (const r of pending) {
            r.abordController.abort()
        }
    }

    /**
     * Ajoute une requête à la queue pour être traitée
     * @param graph_id
     * @param plotly_graph_id
     */
    function enqueueRequest(
        type: "building" | "source",
        graph_id: string,
        plotly_graph_id: string,
        period: [Date, Date],
        freq: Freq,
        callback: (data: any) => void,
        abordController = new AbortController()
    ) {
        const incomingSlot: GraphSlot = { type, graph_id, plotly_graph_id }
        const toAbort: AbortController[] = []
        const newQueue: RequestQueue[] = []

        for (let i = 0; i < queue.value.length; i++) {
            const r = queue.value[i]
            const isHead = i === 0
            if (sameGraphSlot(r, incomingSlot) && !isHead) {
                toAbort.push(r.abordController)
                continue
            }
            newQueue.push(r)
        }
        queue.value = newQueue
        for (const c of toAbort) {
            c.abort()
        }

        if (queue.value[0] && sameGraphSlot(queue.value[0], incomingSlot)) {
            queue.value[0].abordController.abort()
        }

        queue.value.push({
            type,
            graph_id,
            plotly_graph_id,
            freq,
            date_from: period[0],
            date_to: period[1],
            abordController,
            callback,
        })
        processQueue()
    }

    /**
     * Annule toutes les requêtes graphiques en attente ou en cours (AbortSignal),
     * vide la queue et arrête le traitement séquentiel.
     * À appeler à la sortie des pages qui enqueuent des graphiques.
     */
    function cancelPendingGraphRequests() {
        abortAllQueuedGraphRequests()
        isLoading.value = false
    }

    /**
     * Traite la queue des requêtes
     * @returns
     */
    async function processQueue() {
        // Si la queue est vide ou déjà en chargement, on ne fait rien
        if (isLoading.value || queue.value.length === 0) {
            return
        }

        // met la queue en chargement d'une requête
        isLoading.value = true

        const request = queue.value[0]

        if (!request) {
            processQueue()
            return
        }

        try {
            if (request.type === "building") {
                const data = await fetchBuildingChart(request)
                request.callback(data)
            } else if (request.type === "source") {
                const data = await fetchSourceChart(request)
                request.callback(data)
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                request.callback(null)
            }
        } finally {
            isLoading.value = false
            const idx = queue.value.indexOf(request)
            if (idx !== -1) {
                queue.value.splice(idx, 1)
            }
            // attend 500ms avant de traiter la prochaine requête
            setTimeout(() => {
                processQueue()
            }, 500)
        }
    }

    /**
     * Fetch les données du graphique pour un bâtiment
     * @param request La requête à traiter
     * @returns les données du graphique au format JSON string
     */
    async function fetchBuildingChart(request: RequestQueue) {
        // Récupère le dataset à utiliser
        const graphFound = getGraph(request.graph_id)?.graph_list?.find(
            (graph: { id: string }) => graph.id === request.plotly_graph_id
        )

        const datasets = graphFound?.datasets

        try {
            // ajoute un délai aléatoire entre 1 et 5 secondes pour simuler un chargement
            const { data, sources } = await $createGraphByBuildingId(
                buildingStore.building?._id ?? "",
                new Date(
                    Date.UTC(
                        request.date_from.getFullYear(),
                        request.date_from.getMonth(),
                        request.date_from.getDate()
                    )
                )
                    .toISOString()
                    .split("T")[0],
                new Date(
                    Date.UTC(
                        request.date_to.getFullYear(),
                        request.date_to.getMonth(),
                        request.date_to.getDate()
                    )
                )
                    .toISOString()
                    .split("T")[0],
                request.freq,
                datasets as any[],
                {
                    ylabel: graphFound?.ylabel ?? "",
                    abordController: request.abordController,
                    mixed: graphFound?.mixed ?? false,
                }
            )
            return {
                data,
                sources,
            }
        } catch (error) {
            const serviceError = error as ServiceError
            if (serviceError?.code === "aucune-donnee") {
                // Regarde dans la source dans le champ `informations`

                return {
                    data: JSON.stringify({
                        data: "",
                    }),
                    freq: request.freq,
                    availableVariables: [],
                }
            } else {
                throw error
            }
        }
    }

    /**
     * Fetch les données du graphique pour une source
     * @param request La requête à traiter
     * @returns les données du graphique au format JSON string
     */
    async function fetchSourceChart(request: RequestQueue) {
        // Récupère le dataset à utiliser
        const graphFound = getGraph(request.graph_id)?.graph_list?.find(
            (graph: { id: string }) => graph.id === request.plotly_graph_id
        )

        const datasets = graphFound?.datasets

        // Récupère les dates de données disponibles pour les variables de ce graphiques
        const variables = Array.from(
            new Set(
                datasets?.flatMap((dataset) => dataset.type.split(".")[0]) ?? []
            )
        )

        const source = currentSourceId.value
            ? sourceStore.getSourceById(currentSourceId.value)
            : null
        const availableVariablesList = sourceStore.getVariablesBySourceId(
            source?._id_source ?? ""
        )
        const availableVariables = availableVariablesList.filter(
            (type: string) => variables.includes(type)
        )

        // ajoute un délai aléatoire entre 1 et 5 secondes pour simuler un chargement
        try {
            const { data } = await $createGraphBySourceId(
                currentSourceId.value ?? "",
                new Date(
                    Date.UTC(
                        request.date_from.getFullYear(),
                        request.date_from.getMonth(),
                        request.date_from.getDate()
                    )
                )
                    .toISOString()
                    .split("T")[0],
                new Date(
                    Date.UTC(
                        request.date_to.getFullYear(),
                        request.date_to.getMonth(),
                        request.date_to.getDate()
                    )
                )
                    .toISOString()
                    .split("T")[0],
                request.freq,
                datasets as any[],
                {
                    ylabel: graphFound?.ylabel ?? "",
                    abordController: request?.abordController,
                    mixed: graphFound?.mixed ?? false,
                }
            )
            return {
                data,
                freq: request.freq,
                availableVariables: availableVariables,
            }
        } catch (error) {
            const serviceError = error as ServiceError
            if (serviceError?.code === "aucune-donnee") {
                return {
                    data: JSON.stringify({
                        data: "",
                    }),
                    freq: request.freq,
                    availableVariables: availableVariables,
                }
            } else {
                throw error
            }
        }
    }
    /**
     *
     * @param graph_id
     * @param loading
     * @returns
     */
    function addGraph(...graph_id: string[]) {
        for (const id of graph_id) {
            if (!graphs.value.includes(id)) {
                graphs.value.push(id)
            }
        }
    }

    function removeGraph(graph_id: string) {
        const index = graphs.value.findIndex((g) => g === graph_id)
        if (index !== -1) {
            graphs.value.splice(index, 1)
        }
    }

    function isGraphLoading(graph_id: string) {
        return queue.value.some((g) => g.graph_id === graph_id)
    }

    function updateGraphList(graphs_list: string[]) {
        graphs.value = graphs_list
    }

    function clearGraphStorage(
        type: "building" | "source" = "building",
        _id_ref = ""
    ) {
        // Pour les pages spécifiques, on ne fait rien
        const config = CONFIG_GRAPH_PAGE.value.find((config) =>
            route.path.includes(config.pathInclude)
        )
        if (config) {
            return
        }

        if (type === "building") {
            if (_id_ref) {
                storageBuilding.value = storageBuilding.value.filter(
                    (s) => s._id_building !== _id_ref
                )
            } else {
                storageBuilding.value = []
            }
        }
        if (type === "source") {
            if (_id_ref) {
                storageSource.value = storageSource.value.filter(
                    (s) => s._id_source !== _id_ref
                )
            } else {
                storageSource.value = []
            }
        }
    }

    watchDebounced(
        [period, freq],
        ([newPeriod, newFreq]) => {
            // Si la période n'est pas valide, on ne fait rien
            if (!newPeriod?.[0] || !newPeriod?.[1]) {
                return
            }

            if (newFreq === "raw") {
                const maxDays = moment(newPeriod[1]).isLeapYear() ? 366 : 365 // nombre de jours de l'année de newPeriod[1]
                const diffDays = moment(newPeriod[1]).diff(
                    moment(newPeriod[0]),
                    "days"
                )

                if (diffDays > maxDays) {
                    // Limite la période à 2 ans
                    period.value = [
                        moment(newPeriod[1]).subtract(maxDays, "days").toDate(),
                        newPeriod[1],
                    ]
                    // Affiche une notification via le système de toast de PrimeVue
                    busNotification.emit({
                        severity: "warn",
                        title: "Période ajustée",
                        message:
                            "La visualisation en fréquence 'Donnée brute' est limitée à 1 ans. La période a été ajustée automatiquement.",
                        life: 5000,
                    })
                    return
                }
            }

            const dateFromStr = newPeriod[0].toISOString().split("T")[0]
            const dateToStr = newPeriod[1].toISOString().split("T")[0]
            const contextKey = `${newFreq}|${dateFromStr}|${dateToStr}`

            const now = Date.now()
            const dedupeWindowMs = 200
            if (
                graphs.value.length > 0 &&
                lastGraphContextEmit.value?.key === contextKey &&
                now - lastGraphContextEmit.value.at < dedupeWindowMs
            ) {
                return
            }
            lastGraphContextEmit.value = { key: contextKey, at: now }

            abortAllQueuedGraphRequests()

            // On met à jour les paramètres de l'url — sauf sur les pages qui ne
            // portent pas ces paramètres. Ce surveillant s'exécute IMMÉDIATEMENT
            // à la création du store, donc dès qu'une page l'appelle pour une
            // tout autre raison : sans cette garde, la page « Données du
            // compteur » héritait de la période et de la fréquence de la page de
            // données standard, que personne n'avait choisies.
            if (
                urlPorteLesParametresDeGraphe(
                    route.path,
                    pageCompteurV2Servie.value
                )
            ) {
                router.push({
                    query: {
                        ...route.query,
                        freq: newFreq,
                        date_from: dateFromStr,
                        date_to: dateToStr,
                        graph: getGraphQuery.value,
                        action: isActionAuthorised.value ? "true" : "false",
                    },
                })
            }

            // passe le statut de la queue à false pour relancer le traitement
            isLoading.value = false

            if (graphs.value.length > 0) {
                bus.emit("graph:updated")
                bus.emit("update:indicator")
            }

            // traite la queue
            processQueue()
        },
        {
            immediate: true,
            deep: true,
            debounce: 700,
            maxWait: 1800,
        }
    )

    watch(
        graphs,
        (value) => {
            // quand le tableau change, il faut retirer de la queue les requêtes qui ne sont plus valides
            queue.value = queue.value.filter((request) => {
                if (value.includes(request.graph_id)) {
                    return true
                } else {
                    request.abordController.abort()
                    return false
                }
            })
            // Pour les pages spécifiques, on ne fait rien
            const config = CONFIG_GRAPH_PAGE.value.find((config) =>
                route.path.includes(config.pathInclude)
            )
            if (config) {
                return
            }

            if (buildingStore.building) {
                // Met à jour le localStorage pour sauvegarder les graphiques sélectionnés
                const idBuilding = buildingStore.building._id
                const indexBuilding = storageBuilding.value.findIndex(
                    (s) => s._id_building === idBuilding
                )

                if (indexBuilding === -1) {
                    nextTick(() => {
                        if (!buildingStore.building) return
                        storageBuilding.value.push({
                            _id_building: buildingStore.building._id,
                            graphs: value,
                        })
                    })
                } else {
                    storageBuilding.value[indexBuilding].graphs = value
                }
            }
            if (currentSourceId.value) {
                // Met à jour le localStorage pour sauvegarder les graphiques sélectionnés
                const idSource = currentSourceId.value
                const indexSource = storageSource.value.findIndex(
                    (s) => s._id_source === idSource
                )

                if (indexSource === -1) {
                    nextTick(() => {
                        if (!currentSourceId.value) return
                        storageSource.value.push({
                            _id_source: currentSourceId.value,
                            graphs: value,
                        })
                    })
                } else {
                    storageSource.value[indexSource].graphs = value
                }
            }
        },
        { deep: true }
    )

    /**
     * Réinitialise le store à son état initial.
     */
    function $reset() {
        currentSourceId.value = null
        graphs.value = []
        const date_from = route.query.date_from
            ? new Date(route.query.date_from as string)
            : new Date(new Date().setMonth(new Date().getMonth() - 3))
        const date_to = route.query.date_to
            ? new Date(route.query.date_to as string)
            : new Date()
        period.value = [date_from, date_to]
        freq.value = route.query.freq
            ? (route.query.freq as Freq)
            : route.path.includes("building")
              ? "daily"
              : "raw"
        cancelPendingGraphRequests()
        lastGraphContextEmit.value = null
        storageBuilding.value = []
        storageSource.value = []
    }

    return {
        queue,
        graphConfig,
        currentSourceId,
        setCurrentSourceId,
        graphs,
        freq,
        period,
        getGraphQuery,
        isActionAuthorised,
        getOptimalBuildingGraph,
        getOptimalSourceGraph,
        enqueueRequest,
        cancelPendingGraphRequests,
        addGraph,
        updateGraphList,
        removeGraph,
        isGraphLoading,
        clearGraphStorage,
        // Config graphiques (remplie par fetchGraphList)
        fetchGraphList,
        setGraphList,
        isGraphListLoaded,
        getGraph,
        getGraphBuildingConfig,
        getGraphConfigByCategory,
        isFetchingGraphList,
        hasErrorGraphList,
        errorGraphList,
        // Reset
        $reset,
    }
})
